import { useCallback, useEffect, useState } from 'react';
import { auth, User } from '@landr/root-app/auth';
import { SamplesLanguages } from 'types/enums';
import { getIsSupportedLocale } from 'helpers/locale/getIsSupportedLocale';
import { useAppConfig } from '@landr/mfe';

/**
 * Exporting for tests only
 * Do not use directly.
 */
export const useAuthenticationEffects = ({
    isDAWPlugin,
    sendLogout,
}: {
    isDAWPlugin: boolean;
    sendLogout: () => void;
}) => {
    const { name } = useAppConfig();

    const [user, setUser] = useState<User | null>(null);
    const [locale, setLocale] = useState<SamplesLanguages | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        async function getUser() {
            const authUser = await auth.getUser();
            const locale = auth.getLocale();

            const supportedLocale = getIsSupportedLocale(locale)
                ? locale
                : SamplesLanguages.EN;

            setUser(authUser);
            setLocale(supportedLocale);
            setIsLoading(false);
        }

        getUser();
    }, []);

    useEffect(function handleProfileSurveyOnSignup() {
        async function execute() {
            const appState = await auth.getAppState();
            const isSignUp = auth.getIsSignUp();
            const isLandr = name === 'LANDR';

            if (isSignUp && !appState?.skipProfileSurvey && isLandr) {
                auth.redirectToProfileSurvey();
            }
        }

        execute();
    }, []);

    const login = useCallback(() => {
        auth.login({
            mfeName: 'samples',
        });
    }, []);

    const signup = useCallback(() => {
        auth.login({ mfeName: 'samples' }, true);
    }, []);

    const logout = useCallback(() => {
        if (isDAWPlugin) {
            sendLogout();
        }

        auth.logout();
    }, []);

    const isAuthenticated = !!user;
    const isGuestUser = user === null;

    return {
        isLoading,
        isAuthenticated,
        isGuestUser,
        user,
        locale,
        login,
        signup,
        logout,
    };
};

export default useAuthenticationEffects;
